import { useState } from "react";
import { PageHeader, GlassCard, EmptyState, useCrud, ItemDialog, RowActions } from "../_shared";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";

const FIELDS = [
  { key: "name", label: "Full name", required: true, placeholder: "e.g. ICICI Bank" },
  { key: "short", label: "Short label (shown on card)", required: true, placeholder: "ICICI" },
  { key: "domain", label: "Domain (for logo)", required: true, help: "Used to auto-load logo via Google favicon service, e.g. icicibank.com" },
  { key: "logo", label: "Custom logo URL (optional)", type: "image", help: "Override the auto-loaded logo with a hosted image URL." },
  { key: "enabled", label: "Visible on site", type: "switch" },
];

export default function BanksManager() {
  const { items, loading, busy, create, update, remove, move } = useCrud("/admin/cms/banks");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const openNew = () => { setEditing({ enabled: true }); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setOpen(true); };
  const submit = async (form) => {
    if (editing?.id) await update(editing.id, form); else await create(form);
    setOpen(false);
  };

  const resolveLogo = (b) => b.logo || (b.domain ? `https://www.google.com/s2/favicons?sz=128&domain=${b.domain}` : "");

  return (
    <>
      <PageHeader
        title="Bank Partners"
        subtitle="Logos shown in the Bank Partners section on Home and Services."
        actions={<Button onClick={openNew} className="bg-[#0057B8] hover:bg-[#003F87] text-white rounded-full" data-testid="new-bank-btn"><Plus className="w-4 h-4 mr-1" /> New Bank</Button>}
      />
      {loading ? <div className="text-slate-500">Loading…</div> : items.length === 0 ? (
        <EmptyState label="No banks yet." action={<Button onClick={openNew}>Add first bank</Button>} />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((it, i) => (
            <GlassCard key={it.id} className="p-4">
              <div className="flex items-start gap-3">
                <img src={resolveLogo(it)} alt="" className="w-12 h-12 rounded-xl object-contain bg-white border border-slate-200 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900 text-sm truncate">{it.short}</div>
                  <div className="text-xs text-slate-500 truncate">{it.name}</div>
                  <div className="text-[10px] text-slate-400 font-mono truncate">{it.domain}</div>
                  {!it.enabled && <div className="text-[10px] font-semibold bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full mt-1 inline-block">Hidden</div>}
                </div>
              </div>
              <div className="mt-3 flex justify-end">
                <RowActions
                  onEdit={() => openEdit(it)} onDelete={() => remove(it.id)}
                  onUp={() => move(it.id, "up")} onDown={() => move(it.id, "down")}
                  canUp={i > 0} canDown={i < items.length - 1}
                />
              </div>
            </GlassCard>
          ))}
        </div>
      )}
      <ItemDialog
        open={open} onOpenChange={setOpen}
        title={editing?.id ? "Edit Bank" : "New Bank"}
        fields={FIELDS}
        initial={editing || { enabled: true }}
        onSubmit={submit}
        submitting={busy}
      />
    </>
  );
}
